import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Refresh, AlertTriangle } from "@/components/icons";

import { fetchEventLog } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { Card, CardContent } from "@/components/ui/card";
import { DataTableShell } from "@/components/ui/data-table-shell";

/* ---- Event Code Mapping ---- */
const EVENT_CODES: Record<number, { name: string; category: string }> = {
  // Fatal
  1: { name: "Fatal Error", category: "fatal" },
  2: { name: "Watchdog Reset", category: "fatal" },
  3: { name: "Memory Error", category: "fatal" },
  4: { name: "Stack Overflow", category: "fatal" },
  // Error
  10: { name: "Overcurrent", category: "error" },
  11: { name: "Overvoltage", category: "error" },
  12: { name: "Undervoltage", category: "error" },
  13: { name: "Ground Fault", category: "error" },
  14: { name: "Temperature High", category: "error" },
  15: { name: "Temperature Critical", category: "error" },
  16: { name: "Relay Error", category: "error" },
  17: { name: "GFCI Fault", category: "error" },
  18: { name: "Pilot Error", category: "error" },
  19: { name: "Diode Check Fail", category: "error" },
  // Charging
  20: { name: "Charging Started", category: "charging" },
  21: { name: "Charging Stopped", category: "charging" },
  22: { name: "Charging Paused", category: "charging" },
  23: { name: "Charging Resumed", category: "charging" },
  24: { name: "Session Complete", category: "charging" },
  25: { name: "Energy Limit Reached", category: "charging" },
  26: { name: "Time Limit Reached", category: "charging" },
  27: { name: "Vehicle Disconnected", category: "charging" },
  28: { name: "Subsession Start", category: "charging" },
  29: { name: "Subsession End", category: "charging" },
  // Connectivity
  30: { name: "WiFi Connected", category: "connectivity" },
  31: { name: "WiFi Disconnected", category: "connectivity" },
  32: { name: "Server Connected", category: "connectivity" },
  33: { name: "Server Disconnected", category: "connectivity" },
  34: { name: "MQTT Connected", category: "connectivity" },
  35: { name: "MQTT Disconnected", category: "connectivity" },
  36: { name: "OTA Update Start", category: "connectivity" },
  37: { name: "OTA Update Complete", category: "connectivity" },
  38: { name: "OTA Update Failed", category: "connectivity" },
  // Misc
  40: { name: "Boot", category: "misc" },
  41: { name: "Config Changed", category: "misc" },
  42: { name: "Firmware Version", category: "misc" },
  43: { name: "Time Synced", category: "misc" },
  44: { name: "User Auth", category: "misc" },
  45: { name: "Admin Action", category: "misc" },
  // Clear
  50: { name: "Error Cleared", category: "clear" },
  51: { name: "Fault Reset", category: "clear" },
  52: { name: "All Clear", category: "clear" },
};

const CATEGORY_BADGE: Record<string, { variant: "error" | "warning" | "info" | "primary" | "neutral" | "success"; label: string }> = {
  fatal: { variant: "error", label: "Fatal" },
  error: { variant: "warning", label: "Error" },
  charging: { variant: "info", label: "Charging" },
  connectivity: { variant: "primary", label: "Network" },
  misc: { variant: "neutral", label: "Misc" },
  clear: { variant: "success", label: "Clear" },
};

function getEventInfo(code: number): { name: string; category: string } {
  return EVENT_CODES[code] || { name: `Event #${code}`, category: "misc" };
}

function formatDuration(seconds: number | undefined): string {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const eventColumns: ColumnDef<any, any>[] = [
  {
    accessorKey: "timestamp",
    header: "Timestamp",
    cell: ({ getValue }) => (
      <span className="font-mono text-xs whitespace-nowrap">
        {getValue() ? new Date(getValue()).toLocaleString() : "—"}
      </span>
    ),
  },
  {
    accessorKey: "unit_id",
    header: "Unit ID",
    cell: ({ getValue }) => <span className="font-mono text-xs">{getValue() || "—"}</span>,
  },
  {
    accessorKey: "event_code",
    header: "Code",
    cell: ({ getValue }) => <span className="font-mono">{getValue()}</span>,
  },
  {
    id: "event",
    header: "Event",
    cell: ({ row }) => {
      const info = getEventInfo(row.original.event_code);
      return row.original.event_name || info.name;
    },
  },
  {
    id: "category",
    header: "Category",
    cell: ({ row }) => {
      const info = getEventInfo(row.original.event_code);
      const badge = CATEGORY_BADGE[info.category] ?? CATEGORY_BADGE.misc;
      return <Badge variant={badge.variant} className="text-[11px] h-4.5 px-1.5">{badge.label}</Badge>;
    },
  },
  {
    accessorKey: "energy_wh",
    header: "Energy",
    cell: ({ getValue }) => {
      const v = getValue();
      return v != null ? `${(v / 1000).toFixed(2)} kWh` : "—";
    },
  },
  {
    accessorKey: "duration_seconds",
    header: "Duration",
    cell: ({ getValue }) => formatDuration(getValue()),
  },
];

export function EventLogPage() {
  const [unitId, setUnitId] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const q = useQuery({
    queryKey: ["eventlog", { unitId, startTime, endTime }],
    queryFn: () => fetchEventLog({
      unit_id: unitId || undefined,
      start_time: startTime || undefined,
      end_time: endTime || undefined,
    }),
    refetchInterval: autoRefresh ? 10_000 : false,
  });

  const isTcpDisabled = q.data?.error === "TCP data server is disabled";
  const events: any[] = Array.isArray(q.data) ? q.data : q.data?.events || q.data?.event_log || [];

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Event Log</h1>
          <div className="text-sm opacity-70">System events from charger units</div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            Auto: {autoRefresh ? "ON" : "OFF"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
            <Refresh className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[200px]">
              <Label>Unit ID</Label>
              <Input className="mt-1" placeholder="Filter by unit..." value={unitId} onChange={(e) => setUnitId(e.target.value)} />
            </div>
            <div>
              <Label>Start Time</Label>
              <input
                type="datetime-local"
                className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 mt-1"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <Label>End Time</Label>
              <input
                type="datetime-local"
                className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 mt-1"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {isTcpDisabled ? (
        <div className="rounded-md border border-warning/50 bg-warning/10 p-4 text-warning flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          <span>TCP data server is disabled. Event logging is not available.</span>
        </div>
      ) : q.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">Failed to load event log</div>
      ) : (
        <DataTableShell>
          <DataTable
            columns={eventColumns}
            data={events}
            isLoading={q.isLoading}
            tableClassName="text-xs"
            emptyMessage="No events"
          />
        </DataTableShell>
      )}

      <div className="text-center text-xs opacity-50">
        {events.length} events • {autoRefresh ? "Auto-refreshing every 10s" : "Auto-refresh paused"}
      </div>
    </div>
  );
}
